import {
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	ShieldTickSolid,
	StatCard,
	Tooltip
} from '@clickguard/design-system'
import { RefundsByDayDTO } from 'api-models'
import InfoCircle from '@untitled-ui/icons-react/build/esm/InfoCircle'
import moment from 'moment'
import { BarChart } from 'components/charts/bar-chart/bar-chart'
import { RefundsRange } from 'pages/workspace/asset/refunds/types'

const RANGE_TABS: { value: RefundsRange; label: string }[] = [
	{ value: '1m', label: '1M' },
	{ value: '3m', label: '3M' },
	{ value: '6m', label: '6M' }
]

interface InvalidTrafficPanelProps {
	byDay: RefundsByDayDTO[]
	range: RefundsRange
	onRangeChange: (r: RefundsRange) => void
}

/** §5 Reported Invalid Traffic — bar chart with 1M/3M/6M toggle. Spec B5. */
export const InvalidTrafficPanel = ({
	byDay,
	range,
	onRangeChange
}: InvalidTrafficPanelProps) => {
	const chartData = byDay.map((d) => ({
		date: moment(d.date).format('MMM D, YYYY'),
		invalid_clicks: d.invalid_clicks,
		color: 'var(--chart-heatmap-purple-4)'
	}))

	return (
		<StatCard
			title="Reported Invalid Traffic"
			className="h-full refunds-stat-card"
			icon={
				<ShieldTickSolid
					className="w-5 h-5"
					style={{ color: 'var(--color-brand)' }}
				/>
			}
			infoTrigger={
				<Tooltip
					theme="dark"
					position="top"
					portal
					content="Displays the number of invalid clicks reported within each refund request submission."
				>
					<InfoCircle className="w-4 h-4 text-t-gray-hold" />
				</Tooltip>
			}
			trailing={
				<SegmentedTabs
					value={range}
					onChange={(v) => onRangeChange(v as RefundsRange)}
					size="small"
				>
					<SegmentedTabList>
						{RANGE_TABS.map((t) => (
							<SegmentedTab key={t.value} value={t.value} label={t.label} />
						))}
					</SegmentedTabList>
				</SegmentedTabs>
			}
		>
			{chartData.length === 0 ? (
				<div className="h-[186px] flex items-center justify-center text-[14px] text-t-gray-hold">
					No invalid clicks reported in this window.
				</div>
			) : (
				<BarChart
					data={chartData}
					index="date"
					keys={['invalid_clicks']}
					layout="vertical"
					containerClassName="h-[186px]"
					hideAxisLeft={false}
					axisLeftLabel="Invalid Clicks Reported"
					barPadding={0.5}
					borderRadius={6}
					roundTopOnly
					marginBottom={30}
					tooltip={(d) => (
						<span>
							<strong>{d.date}</strong> — {d.invalid_clicks.toLocaleString()}{' '}
							invalid clicks
						</span>
					)}
				/>
			)}
		</StatCard>
	)
}
